import React, { useState, useEffect } from 'react';
import { PasslyTable } from '@app/components';
import { genericErrorToast, infiniteScrollItemsFetchLimit} from '@app/helpers';
import {
    Order,
    OrderType,
    ITableHead,
    IPasswordManagerPolicies,
} from '@app/models';
import { ModalActionsV2, ModalV2 } from '@app/componentsV2';
import { useTypedTranslation } from '@app/hooks';
import { vaultsServiceNew } from '@app/services';

interface IProps {
    vaultId: string;
    handleAddGroups: (ids: string[]) => void;
    groupsCount: number;
    open: boolean;
    closeCallback: Function;
}

const MAX_GROUPS = 50;

const SelectGroups: React.SFC<IProps> = ({ vaultId, handleAddGroups, groupsCount, open, closeCallback }) => {
    const { translation } = useTypedTranslation();

    const [rows, setRows] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [reset, setReset] = useState(0);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState<string>('name');
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitalLoad] = useState(true);
    const [selectedCount, setSelectedCount] = useState(0);
    const [checkboxesDisabled, setCheckboxesDisabled] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(true);

    const setRowsCallback = (rows: any) => {
        setRows(disableExtraRows(rows));
    };

    const disableExtraRows = (items: any) => {
        let selected = 0;

        items.forEach((row: any) => {
            if (row.checked) {
                selected++;
            }
        });

        setSelectedCount(selected);

        const isLimitReached = (groupsCount + selected) >= MAX_GROUPS;

        setCheckboxesDisabled(isLimitReached);

        return items.map((item: any) => {
            return {
                ...item,
                checkboxDisabled: !item.checked && isLimitReached,
            };
        });
    };

    useEffect(() => {
        setDisableSubmit(selectedCount === 0);
    }, [selectedCount, groupsCount]);

    const groupsLoadSuccess = (isReset: boolean) => (response: any) => {
        const count = response.data.count;

        if (count !== undefined) {
            setTotalCount(count);
        }

        const results: IPasswordManagerPolicies[] = response.data.items;

        if (results) {
            if (results.length === 0) {
                setRows([]);
            } else if (isReset) {
                setRows(disableExtraRows(results));
            } else {
                setRows(disableExtraRows(rows.concat(results)));
            }
        }

        setLoading(false);
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        if (initialLoad) {
            setInitalLoad(false);
        }

        if (isReset) {
            setLoading(true);
        }
        vaultsServiceNew.getNotInVaultGroups(orderBy, order, vaultId, infiniteScrollItemsFetchLimit, skipCount, genericErrorToast, groupsLoadSuccess(isReset));
    };

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        setOrder(ascDesc);
        setOrderBy(property);
        setReset(reset + 1);
    };

    const handlePrimaryAction = () => {
        let checkedIds: string[] = [];

        rows.forEach((row: any) => {
            if (row.checked) {
                checkedIds.push(row.id);
            }
        });
        handleAddGroups(checkedIds);
    };

    const headCells: ITableHead[] = [
        {
            id: 'name',
            label: 'GROUPS_NAME',
            type: 'string',
            searchable: true
        }
    ];

    return (
        <ModalV2
            showModal={open}
            showModalCallback={() => closeCallback(false)}
            headerTitle={translation('VAULT_ADD_GROUPS')}
            fixedFooter={true}
            form={
                <>
                    <div className='modal__select-identities'>
                        <PasslyTable
                            rows={rows}
                            setRows={setRowsCallback}
                            order={order}
                            orderBy={orderBy}
                            handleOrderChange={createSortHandler}
                            headerItems={headCells}
                            rowsCount={totalCount}
                            onFetch={fetchMoreData}
                            withCheckboxes={true}
                            hideCheckAllCheckbox={true}
                            loadingData={loading}
                            resetLocal={reset}
                        />
                    </div>

                    <div className='modal__modal-actions'>
                        <ModalActionsV2
                            primaryBtn={translation('VAULT_ADD')}
                            secondaryBtn={translation('CANCEL')}
                            onClickPrimary={handlePrimaryAction}
                            onClickSecondary={() => closeCallback(false)}
                            disabled={disableSubmit}
                        />
                    </div>
                </>
            }
        />
    );
};

export default SelectGroups;
